/*
 * Copyright (c) All right reserved Anvish Inc. 2026
 * This project and any portion thereof may not be reproduced or used in any manner without the express written permission of Anvish Inc.
 */
"use client";
import React, { useState } from "react";
import {
  Navbar,
  NavbarBrand,
  NavbarContent,
  NavbarItem,
  NavbarMenu,
  NavbarMenuItem,
  NavbarMenuToggle,
} from "@heroui/navbar";
import Image from "next/image";
import { motion } from "motion/react";
import NextLink from "next/link";
import { Link } from "@heroui/link";
import { Accordion, AccordionItem } from "@heroui/accordion";

import { useCart } from "@/app/CartContext";

interface NonAuthNavbarProps {
  csrfToken: any;
  navbarData: any;
}

const NonAuthNavbar: React.FC<NonAuthNavbarProps> = ({
  csrfToken,
  navbarData,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const formatedNavbarData = navbarData.items[0];
  const [openSubMenuItem, setOpenSubMenuItem] = useState<any>(["0"]);

  const { cart } = useCart();

  // count total items (not quantities)
  const totalItems = Object.values(cart).reduce((count, category) => {
    return count + Object.keys(category).length;
  }, 0);

  return (
    <Navbar
      className="mx-auto"
      classNames={{
        wrapper: "flex flex-col w-full max-w-auto p-0 h-auto gap-0",
      }}
      isMenuOpen={isMenuOpen}
      onMenuOpenChange={setIsMenuOpen}
    >
      <NavbarContent className="flex items-center !justify-between w-full px-4 md:px-14 xl:px-[100px] max-w-[1440px] py-5 md:py-4">
        <NextLink
          className="flex items-center shrink md:shrink-0 grow-0"
          href="/"
        >
          <NavbarBrand>
            <img
              alt={formatedNavbarData.navbar_logo.title}
              className="max-h-12 max-w-24 xs:max-w-full"
              src={formatedNavbarData.navbar_logo.meta.download_url}
            />
          </NavbarBrand>
        </NextLink>
        <div className="flex items-center gap-2 xs:gap-3 md:gap-7">
          <div className="hidden lg:flex items-center gap-2 xs:gap-3 md:gap-7">
            {formatedNavbarData.secondary_nav_items.map(
              (linkItem: any, index: number) => (
                <Link
                  key={index}
                  isBlock
                  className="items-center text-sm md:text-medium "
                  color="foreground"
                  href={linkItem.value.link}
                >
                  <NavbarItem>{linkItem.value.title}</NavbarItem>
                </Link>
              ),
            )}
          </div>
          {/*<NavbarItem className="rounded-full hover:bg-default-200 cursor-pointer shrink-0">*/}
          {/*  <Image*/}
          {/*    alt="Search"*/}
          {/*    className="w-9 h-9 p-2"*/}
          {/*    height="24"*/}
          {/*    src="/icons/magnifier.svg"*/}
          {/*    width="24"*/}
          {/*  />*/}
          {/*</NavbarItem>*/}
          <Link href="/dashboard">
            <NavbarItem className="rounded-full hover:bg-default-200 cursor-pointer shrink-0">
              <Image
                alt="Dashboard"
                className="w-9 h-9 p-2"
                height="24"
                src="/icons/user_circle.svg"
                width="24"
              />
            </NavbarItem>
          </Link>

          <Link href="/estore/cart">
            <NavbarItem className="rounded-full hover:bg-default-200 cursor-pointer shrink-0 relative">
              <Image
                alt="Cart"
                className="w-9 h-9 p-2"
                height="24"
                src="/icons/bag.svg"
                width="24"
              />
              <motion.div
                animate={{
                  scale: totalItems > 0 ? 1 : 0,
                }}
                className="absolute flex items-center justify-center text-xs bg-ax-secondary text-white rounded-full w-4 h-4 -top-[1px] -right-[1px] overflow-clip"
                initial={{
                  scale: 0,
                }}
                transition={{ type: "spring", stiffness: 300, damping: 15 }}
              >
                {totalItems}
              </motion.div>
            </NavbarItem>
          </Link>
          <NavbarMenuToggle
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            className="flex items-center justify-center w-7 h-7 cursor-pointer lg:hidden"
            icon={
              <motion.div
                className="relative flex flex-col items-end justify-center translate-0 w-6 h-6"
                layoutId="menuToggle"
              >
                <motion.div
                  animate={{
                    rotateZ: isMenuOpen ? 135 : 0,
                    y: isMenuOpen ? 0 : -6,
                    width: isMenuOpen ? 24 : 20,
                  }}
                  className="absolute h-[2px] rounded-full bg-black"
                  initial={{
                    rotateZ: 0,
                    y: 0,
                    width: 20,
                  }}
                  transition={{ duration: 0.3 }}
                />
                <motion.div
                  animate={{
                    y: isMenuOpen ? 0 : 0,
                    width: isMenuOpen ? 24 : 24,
                    x: isMenuOpen ? 24 : 0,
                    opacity: isMenuOpen ? 0 : 1,
                  }}
                  className="absolute h-[2px] rounded-full bg-black"
                  initial={{
                    x: 0,
                    y: 0,
                    width: 24,
                    opacity: 1,
                  }}
                  transition={{ duration: 0.3 }}
                />
                <motion.div
                  animate={{
                    rotateZ: isMenuOpen ? 45 : 0,
                    y: isMenuOpen ? 0 : 6,
                    width: isMenuOpen ? 24 : 14,
                  }}
                  className="absolute h-[2px] rounded-full bg-black"
                  initial={{
                    rotateZ: 0,
                    y: 0,
                    width: 14,
                  }}
                  transition={{ duration: 0.3 }}
                />
              </motion.div>
            }
          />
        </div>
      </NavbarContent>
      <NavbarContent className="w-full !bg-[#F8F8F8] hidden lg:flex">
        <div className="flex items-center !justify-between w-full max-w-[1440px] px-10 md:px-20 xl:px-[120px] py-5 mx-auto">
          {formatedNavbarData.main_nav_items.map(
            (linkItem: any, index: number) => (
              <Link
                key={index}
                isBlock
                className="flex items-center text-sm md:text-medium"
                color="foreground"
                href={linkItem.value.link}
              >
                <NavbarItem>{linkItem.value.title}</NavbarItem>
              </Link>
            ),
          )}
        </div>
      </NavbarContent>
      <NavbarMenu className="p-0 mt-[13px] xs:mt-[25px] md:mt-[17px] w-full bg-white">
        <div className="mt-5 px-4 md:px-14 xl:px-[100px]">
          {formatedNavbarData.mobile_nav_items.map(
            (linkItem: any, index: number) => {
              return linkItem.type === "sub_menu_item" ? (
                <NavbarMenuItem
                  key={index}
                  className="flex items-center text-lg"
                >
                  <Accordion className="p-0" variant="light">
                    <AccordionItem
                      key={linkItem.id}
                      className="w-full"
                      classNames={{
                        trigger: `p-4 border-b-[1px] ${
                          openSubMenuItem === linkItem.id
                            ? "border-black/50"
                            : "border-black/10"
                        }`,
                        title: `text-lg ${
                          openSubMenuItem === linkItem.id
                            ? "font-medium"
                            : "font-normal"
                        }`,
                      }}
                      title={linkItem.value.title}
                      onPress={() =>
                        setOpenSubMenuItem((prev: any) => {
                          if (prev === linkItem.id) {
                            return 0;
                          }

                          return linkItem.id;
                        })
                      }
                    >
                      <div
                        className={`flex flex-col w-full ${openSubMenuItem === linkItem.id ? "border-b-[1px] border-black/50" : "border-b-0"}`}
                      >
                        {linkItem.value.sub_menu_items.map(
                          (subLinkItem: any, subIndex: number) => (
                            <Link
                              key={subIndex}
                              className="flex items-center text-sm md:text-medium p-4 border-b-[1px] border-black/10"
                              color="foreground"
                              href={subLinkItem.value.link}
                              onPress={() => setIsMenuOpen(false)}
                            >
                              <div>{subLinkItem.value.title}</div>
                            </Link>
                          ),
                        )}
                      </div>
                    </AccordionItem>
                  </Accordion>
                </NavbarMenuItem>
              ) : (
                <Link
                  key={index}
                  className="flex items-center text-sm md:text-medium p-4 border-b-[1px] border-black/10 bg-white z-10"
                  color="foreground"
                  href={linkItem.value.link}
                  onPress={() => setIsMenuOpen(false)}
                >
                  <NavbarMenuItem>{linkItem.value.title}</NavbarMenuItem>
                </Link>
              );
            },
          )}
        </div>
      </NavbarMenu>
    </Navbar>
  );
};

export default NonAuthNavbar;
